import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from '@dr.pogodin/react-helmet';

const EFFECTIVE_DATE = 'June 25, 2026'

interface SectionProps {
  id: string
  title: string
  children: React.ReactNode
}

function Section({ id, title, children }: SectionProps) {
  return (
    <section id={id} style={{ scrollMarginTop: '6rem', marginBottom: '2rem' }}>
      <h2 style={{ fontSize: '1.25rem', fontWeight: 600, color: '#1e293b', marginBottom: '0.75rem', paddingBottom: '0.5rem', borderBottom: '1px solid #f1f5f9' }}>
        {title}
      </h2>
      <div style={{ color: '#64748b', lineHeight: 1.7 }}>{children}</div>
    </section>
  )
}

// @ts-expect-error - Reserved for future table of contents feature
const toc = [
  { id: 'introduction', label: 'Introduction' },
  { id: 'information-we-collect', label: 'Information We Collect' },
  { id: 'how-we-use-information', label: 'How We Use Information' },
  { id: 'sms-communications', label: 'SMS Communications' },
  { id: 'sharing-of-information', label: 'Sharing of Information' },
  { id: 'data-retention', label: 'Data Retention' },
  { id: 'data-security', label: 'Data Security' },
  { id: 'your-rights', label: 'Your Rights' },
  { id: 'third-party-links', label: 'Third-Party Links' },
  { id: 'childrens-privacy', label: "Children's Privacy" },
  { id: 'changes-to-policy', label: 'Changes to This Policy' },
  { id: 'contact', label: 'Contact Information' },
]

export default function PrivacyPolicy() {
  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(to bottom, #f8fafc, white)', fontFamily: 'system-ui, -apple-system, sans-serif' }}>
      <Helmet>
        <title>Privacy Policy | Atomic Tattoo Removal</title>
        <meta name="description" content="Privacy Policy for Atomic Tattoo Removal. Learn how we collect, use, and protect your personal information." />
      </Helmet>

      {/* Minimal nav */}
      <header style={{ background: 'rgba(255,255,255,0.9)', backdropFilter: 'blur(12px)', borderBottom: '1px solid #f1f5f9', position: 'sticky', top: 0, zIndex: 50 }}>
        <div style={{ maxWidth: '72rem', margin: '0 auto', padding: '0 1.5rem', height: '4rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: 700, fontSize: '1.125rem', color: '#1e293b', textDecoration: 'none' }}>
            Atomic Tattoo Removal
          </Link>
          <Link
            to="/"
            style={{ fontSize: '0.875rem', color: '#64748b', textDecoration: 'none' }}
          >
            &larr; Back to Home
          </Link>
        </div>
      </header>

      <div style={{ maxWidth: '72rem', margin: '0 auto', padding: '4rem 1.5rem' }}>
        {/* Page header */}
        <div style={{ marginBottom: '3rem', maxWidth: '42rem' }}>
          <span style={{ display: 'inline-block', fontSize: '0.75rem', fontWeight: 600, letterSpacing: '0.1em', color: '#6366f1', textTransform: 'uppercase', marginBottom: '0.75rem' }}>
            Legal
          </span>
          <h1 style={{ fontSize: '2.25rem', fontWeight: 700, color: '#1e293b', marginBottom: '1rem', lineHeight: 1.2 }}>
            Privacy Policy
          </h1>
          <p style={{ color: '#64748b', fontSize: '0.875rem' }}>
            Effective Date: <span style={{ fontWeight: 500, color: '#475569' }}>{EFFECTIVE_DATE}</span>
          </p>
          <p style={{ marginTop: '1rem', color: '#64748b', fontSize: '1rem', lineHeight: 1.7 }}>
            Please read this policy carefully. It explains how Atomic Tattoo Removal collects, uses, and
            protects information in connection with our tattoo removal services and communications.
          </p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '4rem' }}>
          {/* Main content */}
          <main style={{ maxWidth: '50rem' }}>
            {/* 1. Introduction */}
            <Section id="introduction" title="1. Introduction">
              <p>
                Atomic Tattoo Removal ("<strong style={{ color: '#475569' }}>Atomic Tattoo Removal</strong>," "we," "us," or "our") operates a laser tattoo removal clinic providing professional tattoo removal services to clients in Wrentham, MA and surrounding areas. We are committed to protecting your privacy and maintaining the security of your personal information.
              </p>
              <p>
                This Privacy Policy describes how we collect, use, disclose, and safeguard
                personal information in connection with our services, website located at{' '}
                <a
                  href="https://atomictattooremoval.com"
                  style={{ color: '#6366f1', textDecoration: 'underline' }}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  https://atomictattooremoval.com
                </a>
                , and communications (including phone calls and text messages). By accessing or using our services, you agree to the practices described in
                this policy.
              </p>
            </Section>

            {/* 2. Information We Collect */}
            <Section id="information-we-collect" title="2. Information We Collect">
              <p>
                We collect information necessary to provide our services and communicate with our clients. This includes:
              </p>
              <ul style={{ listStyle: 'disc', paddingLeft: '1.25rem', marginTop: '0.75rem', lineHeight: 1.8 }}>
                <li>
                  <span style={{ fontWeight: 500, color: '#475569' }}>Personal contact information</span>{' '}
                  — name, email address, phone number, and mailing address provided during consultation booking or when contacting us.
                </li>
                <li>
                  <span style={{ fontWeight: 500, color: '#475569' }}>Treatment information</span>{' '}
                  — information about your tattoo, photos submitted for evaluation, medical history relevant to treatment, and treatment records.
                </li>
                <li>
                  <span style={{ fontWeight: 500, color: '#475569' }}>SMS communication content</span>{' '}
                  — the content of text messages sent and received when you communicate with us via SMS.
                </li>
                <li>
                  <span style={{ fontWeight: 500, color: '#475569' }}>Call metadata</span> — including
                  call time, duration, and associated phone numbers when you call our business.
                </li>
                <li>
                  <span style={{ fontWeight: 500, color: '#475569' }}>Payment information</span>{' '}
                  — billing information and payment history for services rendered.
                </li>
                <li>
                  <span style={{ fontWeight: 500, color: '#475569' }}>Website usage data</span>{' '}
                  — IP address, browser type, device information, pages visited, and session activity on our website.
                </li>
              </ul>
            </Section>

            {/* 3. How We Use Information */}
            <Section id="how-we-use-information" title="3. How We Use Information">
              <p>We use the information we collect to:</p>
              <ul style={{ listStyle: 'disc', paddingLeft: '1.25rem', marginTop: '0.75rem', lineHeight: 1.8 }}>
                <li>
                  Provide, maintain, and improve our tattoo removal services and customer experience.
                </li>
                <li>
                  Schedule and confirm consultations and treatment appointments.
                </li>
                <li>
                  Communicate with you via phone, SMS, email, or other channels regarding your appointments, treatment, and inquiries.
                </li>
                <li>
                  Process payments and manage billing.
                </li>
                <li>
                  Send appointment reminders, follow-up messages, and treatment updates.
                </li>
                <li>
                  Respond to customer service requests and provide support.
                </li>
                <li>
                  Monitor and analyze website usage to improve our online presence.
                </li>
                <li>
                  Comply with applicable legal obligations and respond to lawful requests from
                  regulatory or government authorities.
                </li>
              </ul>
            </Section>

            {/* 4. SMS Communications */}
            <Section id="sms-communications" title="4. SMS Communications">
              <p>
                Atomic Tattoo Removal sends SMS text messages to clients who have opted in to receive them. We obtain your consent in one of the following ways:
              </p>
              <ul style={{ listStyle: 'disc', paddingLeft: '1.25rem', marginTop: '0.75rem', lineHeight: 1.8 }}>
                <li>
                  <span style={{ fontWeight: 500, color: '#475569' }}>Appointment reminders (intake form opt-in)</span>{' '}
                  — When you book an appointment and complete our patient intake form, you may opt in to SMS appointment reminders by checking the "Subscribe to SMS Appointment Reminders" box. This box is not pre-selected; you must actively check it to receive reminders.
                </li>
                <li>
                  <span style={{ fontWeight: 500, color: '#475569' }}>Conversational messages</span>{' '}
                  — If you text our business phone number, you consent to receive SMS replies from us in connection with your inquiry, appointment, or treatment.
                </li>
              </ul>

              <p style={{ marginTop: '1rem' }}>Types of messages you may receive include:</p>
              <ul style={{ listStyle: 'disc', paddingLeft: '1.25rem', marginTop: '0.75rem', lineHeight: 1.8 }}>
                <li>
                  Appointment reminders for upcoming consultations or treatment sessions.
                </li>
                <li>
                  Appointment confirmations and notices of scheduling changes.
                </li>
                <li>
                  Replies to questions you send us, treatment updates, and aftercare instructions.
                </li>
              </ul>

              <div style={{ marginTop: '1.25rem', borderRadius: '0.75rem', background: '#eef2ff', border: '1px solid #c7d2fe', padding: '1.25rem', fontSize: '0.875rem' }}>
                <p style={{ fontWeight: 600, color: '#475569', fontSize: '1rem', marginBottom: '0.75rem' }}>
                  Important SMS Disclosures
                </p>
                <p>
                  <span style={{ fontWeight: 500, color: '#475569' }}>Message frequency varies</span>{' '}
                  depending on your appointments and interactions with our clinic.
                </p>
                <p style={{ marginTop: '0.75rem' }}>
                  <span style={{ fontWeight: 500, color: '#475569' }}>
                    Message and data rates may apply
                  </span>{' '}
                  based on your mobile carrier plan.
                </p>
                <p style={{ marginTop: '0.75rem' }}>
                  To <span style={{ fontWeight: 500, color: '#475569' }}>opt out</span> of SMS
                  messages at any time, reply <span style={{ fontFamily: 'monospace', fontWeight: 700, color: '#6366f1' }}>STOP</span>{' '}
                  to any message. You will receive a single confirmation message and no further
                  messages will be sent.
                </p>
                <p style={{ marginTop: '0.75rem' }}>
                  For <span style={{ fontWeight: 500, color: '#475569' }}>assistance</span>, reply{' '}
                  <span style={{ fontFamily: 'monospace', fontWeight: 700, color: '#6366f1' }}>HELP</span> to any
                  message or contact us at{' '}
                  <a
                    href="mailto:craig@atomictattooremoval.com"
                    style={{ color: '#6366f1', textDecoration: 'underline' }}
                  >
                    craig@atomictattooremoval.com
                  </a>
                  .
                </p>
              </div>

              <p style={{ marginTop: '1rem' }}>
                Consent to receive SMS messages is not a condition of purchasing any service. We do not send marketing or promotional text messages, and we do not share your mobile number or SMS opt-in with third parties for their own marketing purposes. Mobile information is shared only with the SMS and telephony providers that help us deliver these messages on our behalf.
              </p>
            </Section>

            {/* 5. Sharing of Information */}
            <Section id="sharing-of-information" title="5. Sharing of Information">
              <p>
                <span style={{ fontWeight: 500, color: '#475569' }}>
                  We do not sell personal information.
                </span>{' '}
                We do not share personal information with third parties for their own
                marketing purposes. We may share information in the following limited
                circumstances:
              </p>
              <ul style={{ listStyle: 'disc', paddingLeft: '1.25rem', marginTop: '0.75rem', lineHeight: 1.8 }}>
                <li>
                  <span style={{ fontWeight: 500, color: '#475569' }}>Service providers</span> —
                  third-party vendors who assist us with appointment scheduling, payment processing, SMS communications, website hosting, and other operational needs. These providers are bound by confidentiality obligations.
                </li>
                <li>
                  <span style={{ fontWeight: 500, color: '#475569' }}>SMS and telephony providers</span> —
                  services used to facilitate text message and call communications on our behalf.
                </li>
                <li>
                  <span style={{ fontWeight: 500, color: '#475569' }}>
                    Legal and regulatory requirements
                  </span>{' '}
                  — if required by law, court order, or governmental authority, or to protect
                  the rights, property, or safety of Atomic Tattoo Removal, our clients, or the
                  public.
                </li>
                <li>
                  <span style={{ fontWeight: 500, color: '#475569' }}>Business transfers</span> — in
                  connection with a merger, acquisition, or sale of assets, where personal
                  information may be transferred as a business asset.
                </li>
              </ul>
            </Section>

            {/* 6. Data Retention */}
            <Section id="data-retention" title="6. Data Retention">
              <p>
                We retain personal information only for as long as necessary to fulfill the
                purposes outlined in this Privacy Policy. Specifically:
              </p>
              <ul style={{ listStyle: 'disc', paddingLeft: '1.25rem', marginTop: '0.75rem', lineHeight: 1.8 }}>
                <li>
                  Treatment records and client information are retained as required by applicable healthcare record-keeping laws and professional standards.
                </li>
                <li>
                  SMS message content and call records are retained as needed for quality assurance and to support our service delivery.
                </li>
                <li>
                  Payment and billing information is retained as required for accounting, tax, and legal compliance purposes.
                </li>
                <li>
                  Data may be retained longer where required to comply with applicable laws,
                  regulations, or lawful obligations.
                </li>
              </ul>
              <p>
                When data is no longer needed, we take reasonable steps to securely delete or
                anonymize it.
              </p>
            </Section>

            {/* 7. Data Security */}
            <Section id="data-security" title="7. Data Security">
              <p>
                We implement reasonable and appropriate administrative, technical, and
                physical safeguards to protect personal information against unauthorized
                access, disclosure, alteration, or destruction. These measures include
                encrypted data transmission, access controls, secure storage, and regular security reviews.
              </p>
              <p>
                However, no method of transmission over the internet or electronic storage is
                completely secure. While we strive to protect your information using industry-standard practices, we cannot
                guarantee absolute security. In the event of a data breach that affects your
                rights, we will notify affected parties as required by applicable
                law.
              </p>
            </Section>

            {/* 8. Your Rights */}
            <Section id="your-rights" title="8. Your Rights">
              <p>
                Depending on your location, you may have rights regarding your personal
                information, including:
              </p>
              <ul style={{ listStyle: 'disc', paddingLeft: '1.25rem', marginTop: '0.75rem', lineHeight: 1.8 }}>
                <li>
                  <span style={{ fontWeight: 500, color: '#475569' }}>Access</span> — the right to
                  request a copy of the personal information we hold about you.
                </li>
                <li>
                  <span style={{ fontWeight: 500, color: '#475569' }}>Correction</span> — the right
                  to request correction of inaccurate or incomplete information.
                </li>
                <li>
                  <span style={{ fontWeight: 500, color: '#475569' }}>Deletion</span> — the right to
                  request deletion of your personal information, subject to applicable legal
                  obligations and record-keeping requirements.
                </li>
                <li>
                  <span style={{ fontWeight: 500, color: '#475569' }}>Opt-out of SMS</span> — the
                  right to opt out of receiving SMS messages at any time by replying{' '}
                  <span style={{ fontFamily: 'monospace', fontWeight: 700, color: '#6366f1' }}>STOP</span> to any
                  message.
                </li>
              </ul>
              <p style={{ marginTop: '0.75rem' }}>
                To exercise these rights, please contact us using the information provided below. We will respond to verified requests in accordance with applicable law.
              </p>
            </Section>

            {/* 9. Third-Party Links */}
            <Section id="third-party-links" title="9. Third-Party Links">
              <p>
                Our website may contain links to third-party websites or services. These links are provided for convenience and informational purposes
                only. Atomic Tattoo Removal does not control and is not responsible for the privacy
                practices or content of any third-party site. We encourage you to review the
                privacy policies of any external sites you visit.
              </p>
            </Section>

            {/* 10. Children's Privacy */}
            <Section id="childrens-privacy" title="10. Children's Privacy">
              <p>
                Our services are intended for adults aged 18 and older. We do not
                knowingly collect personal information from individuals under the age of 18. If we become
                aware that we have inadvertently collected such information, we will take
                prompt steps to delete it. If you believe a minor's information has been
                submitted, please contact us at{' '}
                <a
                  href="mailto:craig@atomictattooremoval.com"
                  style={{ color: '#6366f1', textDecoration: 'underline' }}
                >
                  craig@atomictattooremoval.com
                </a>
                .
              </p>
            </Section>

            {/* 11. Changes to This Policy */}
            <Section id="changes-to-policy" title="11. Changes to This Policy">
              <p>
                We may update this Privacy Policy from time to time to reflect changes in our
                practices, technology, legal requirements, or for other operational reasons.
                When we make material changes, we will update the "Effective Date" at the top
                of this page and, where appropriate, notify clients via email or a prominent
                notice on our website.
              </p>
              <p>
                Your continued use of our services following the posting of an updated
                Privacy Policy constitutes your acceptance of the revised terms. We encourage
                you to review this policy periodically.
              </p>
            </Section>

            {/* 12. Contact Information */}
            <Section id="contact" title="12. Contact Information">
              <p>
                If you have questions, concerns, or requests regarding this Privacy Policy or
                our handling of personal information, please contact us:
              </p>
              <div style={{ marginTop: '1rem', borderRadius: '0.75rem', border: '1px solid #e2e8f0', background: '#f8fafc', padding: '1.5rem', fontSize: '0.875rem', lineHeight: 1.6 }}>
                <p style={{ fontSize: '1rem', fontWeight: 600, color: '#1e293b' }}>Atomic Tattoo Removal</p>
                <p style={{ marginTop: '0.5rem' }}>
                  <span style={{ color: '#64748b' }}>Address:</span> 78 South St #201, Wrentham, MA 02093
                </p>
                <p>
                  <span style={{ color: '#64748b' }}>Phone:</span> (508) 203-1342
                </p>
                <p>
                  <span style={{ color: '#64748b' }}>Website:</span>{' '}
                  <a
                    href="https://atomictattooremoval.com"
                    style={{ color: '#6366f1', textDecoration: 'underline' }}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    https://atomictattooremoval.com
                  </a>
                </p>
                <p>
                  <span style={{ color: '#64748b' }}>Email:</span>{' '}
                  <a
                    href="mailto:craig@atomictattooremoval.com"
                    style={{ color: '#6366f1', textDecoration: 'underline' }}
                  >
                    craig@atomictattooremoval.com
                  </a>
                </p>
              </div>
            </Section>

            {/* Bottom divider */}
            <div style={{ paddingTop: '1.5rem', borderTop: '1px solid #f1f5f9', fontSize: '0.75rem', color: '#cbd5e1' }}>
              &copy; {new Date().getFullYear()} Atomic Tattoo Removal. All rights reserved. &mdash;{' '}
              Effective {EFFECTIVE_DATE}
            </div>
          </main>
        </div>
      </div>
    </div>
  )
}
